import {
    Badge,
    Button,
    Card,
    CardBody,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    SimpleGrid,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Text,
    useColorModeValue,
    VStack,
} from '@chakra-ui/react';
import React, { useRef, useState } from 'react';
import { FiCalendar, FiFileText, FiFilter, FiSearch, FiUserCheck, FiUsers, FiUserX, FiX } from 'react-icons/fi';
import { RegistrationFilters as FilterType, RegistrationListResponse } from '../hooks/useRegistrationList';

interface RegistrationFiltersProps {
  filters: FilterType;
  onFilter: (filters: FilterType) => void;
  onClearFilters: () => void;
  totalRecords: number;
  currentRecords: number;
  stats: RegistrationListResponse['stats'] | null;
  isLoading: boolean;
}

const storeOptions = [
  { value: '', label: 'Todas as lojas' },
  { value: 'loja-principal', label: 'Loja Principal' },
  { value: 'loja-secundaria', label: 'Loja Secundária' },
  { value: 'italia-consulado', label: 'Itália Consulado' },
];

const RegistrationFilters: React.FC<RegistrationFiltersProps> = ({
  filters,
  onFilter,
  onClearFilters,
  totalRecords,
  currentRecords,
  stats,
  isLoading
}) => {
  const [localFilters, setLocalFilters] = useState<FilterType>(filters);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleInputChange = (field: keyof FilterType, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const handleSearch = () => {
    onFilter(localFilters);
  };

  const handleClear = () => {
    setLocalFilters({});
    onClearFilters();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDocument = (value: string) => {
    // Remove tudo que não for número
    const numbers = value.replace(/\D/g, '');
    
    // Aplica máscara de CPF (11 dígitos) ou CNPJ (14 dígitos)
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    handleInputChange('userDocument', e.target.value);
  };

  const activeFiltersCount = Object.values(localFilters).filter(Boolean).length;
  const hasActiveFilters = activeFiltersCount > 0;

  return (
    <VStack spacing={6}>
      {/* Estatísticas */}
      {stats && (
        <Card w="100%">
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Stat textAlign="center">
                <StatLabel display="flex" alignItems="center" justifyContent="center">
                  <Icon as={FiUsers} mr={2} color="blue.500" />
                  Total
                </StatLabel>
                <StatNumber color="blue.600">{totalRecords.toLocaleString()}</StatNumber>
                <StatHelpText>Usuários inscritos</StatHelpText>
              </Stat>
              
              <Stat textAlign="center">
                <StatLabel display="flex" alignItems="center" justifyContent="center">
                  <Icon as={FiUserCheck} mr={2} color="green.500" />
                  Com Check-in
                </StatLabel>
                <StatNumber color="green.600">{stats.totalComCheckin?.toLocaleString()}</StatNumber>
                <StatHelpText>Check-in realizado</StatHelpText>
              </Stat>
              
              <Stat textAlign="center">
                <StatLabel display="flex" alignItems="center" justifyContent="center">
                  <Icon as={FiUserX} mr={2} color="red.500" />
                  Sem Check-in
                </StatLabel>
                <StatNumber color="red.600">{stats.totalSemCheckin?.toLocaleString()}</StatNumber>
                <StatHelpText>Pendente check-in</StatHelpText>
              </Stat>
              
              <Stat textAlign="center">
                <StatLabel display="flex" alignItems="center" justifyContent="center">
                  <Icon as={FiFileText} mr={2} color="purple.500" />
                  Carregados
                </StatLabel>
                <StatNumber color="purple.600">{currentRecords.toLocaleString()}</StatNumber>
                <StatHelpText>Na lista atual</StatHelpText>
              </Stat>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Filtros */}
      <Card w="100%">
        <CardBody>
          <VStack spacing={4}>
            {/* Título e Contador */}
            <HStack w="100%" justify="space-between" align="center">
              <HStack spacing={2}>
                <Text fontSize="lg" fontWeight="bold" color="gray.700">
                  Usuários Inscritos
                </Text>
                {hasActiveFilters && (
                  <Badge colorScheme="blue" variant="solid">
                    {activeFiltersCount} filtro{activeFiltersCount > 1 ? 's' : ''}
                  </Badge>
                )}
              </HStack>
              <Text fontSize="sm" color="gray.600">
                Mostrando <strong>{currentRecords.toLocaleString()}</strong> de{' '}
                <strong>{totalRecords.toLocaleString()}</strong> registros
              </Text>
            </HStack>

            {/* Campos de Filtro */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} w="100%">
              {/* Busca por Nome */}
              <InputGroup>
                <InputLeftElement>
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  ref={searchInputRef}
                  placeholder="Buscar por nome..."
                  value={localFilters.userName || ''}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                  onKeyPress={handleKeyPress}
                  bg={bgColor}
                  borderColor={borderColor}
                  _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                />
              </InputGroup>

              {/* Busca por Documento */}
              <InputGroup>
                <InputLeftElement>
                  <Icon as={FiFileText} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="CPF/CNPJ..."
                  value={localFilters.userDocument || ''}
                  onChange={handleDocumentChange}
                  onKeyPress={handleKeyPress}
                  bg={bgColor}
                  borderColor={borderColor}
                  _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                  maxLength={18} // CPF: 14 chars, CNPJ: 18 chars com máscara
                />
              </InputGroup>

              {/* Seletor de Loja */}
              {/* <Select
                placeholder="Selecionar loja"
                value={localFilters.store || ''}
                onChange={(e) => handleInputChange('store', e.target.value)}
                bg={bgColor}
                borderColor={borderColor}
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
              >
                {storeOptions.slice(1).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select> */}

              {/* Data Início */}
              <InputGroup>
                <InputLeftElement>
                  <Icon as={FiCalendar} color="gray.400" />
                </InputLeftElement>
                <Input
                  type="date"
                  placeholder="Data início"
                  value={localFilters.startDate || ''}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  bg={bgColor}
                  borderColor={borderColor}
                  _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                />
              </InputGroup>

              {/* Data Fim */}
              <InputGroup>
                <InputLeftElement>
                  <Icon as={FiCalendar} color="gray.400" />
                </InputLeftElement>
                <Input
                  type="date"
                  placeholder="Data fim"
                  value={localFilters.endDate || ''}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  bg={bgColor}
                  borderColor={borderColor}
                  _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                />
              </InputGroup>
            </SimpleGrid>

            {/* Botões de Ação */}
            <HStack spacing={4} w="100%" justify="flex-end">
              <Button
                leftIcon={<FiX />}
                variant="outline"
                onClick={handleClear}
                isDisabled={!hasActiveFilters || isLoading}
                size="md"
              >
                Limpar Filtros
              </Button>
              <Button
                leftIcon={<FiFilter />}
                colorScheme="blue"
                onClick={handleSearch}
                isLoading={isLoading}
                size="md"
              >
                Filtrar
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default RegistrationFilters; 